/**
 * Hook para métricas de vendas e rankings
 *
 * Responsabilidade: Calcular vendas por mês, por canal, rankings de agentes e LTV
 */

import { useMemo } from 'react';
import { Lead, CrmUser, Contact } from '../../../types';

export const useSalesMetrics = (
    leads: Lead[],
    filteredLeads: Lead[],
    crmUsers: CrmUser[],
    contacts: Contact[]
) => {
    // Vendas por mês (últimos 12 meses)
    const salesByMonth = useMemo(() => {
        const today = new Date();
        const startPeriod = new Date();
        startPeriod.setMonth(today.getMonth() - 11);
        startPeriod.setDate(1);
        startPeriod.setHours(0, 0, 0, 0);

        const sales: { [key: string]: { value: number; negotiation: number; sortDate: number } } = {};
        let totalWonLast12 = 0;

        // Inicializa últimos 12 meses
        for (let i = 0; i < 12; i++) {
            const d = new Date(startPeriod);
            d.setMonth(d.getMonth() + i);
            const key = d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
            sales[key] = { value: 0, negotiation: 0, sortDate: d.getTime() };
        }

        leads.forEach(lead => {
            const d = new Date(lead.entryDate);
            if (d >= startPeriod) {
                const key = d.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
                const val = Number(lead.value) || 0;

                if (sales[key]) {
                    if (lead.status === 'won') {
                        sales[key].value += val;
                        totalWonLast12 += val;
                    } else if (lead.status !== 'lost') {
                        sales[key].negotiation += val;
                    }
                }
            }
        });

        const average = totalWonLast12 / 12;

        return Object.values(sales)
            .sort((a, b) => a.sortDate - b.sortDate)
            .map(item => ({
                name: new Date(item.sortDate).toLocaleDateString('pt-BR', { month: 'short' }),
                value: item.value,
                negotiation: item.negotiation,
                average
            }));
    }, [leads]);

    const salesByChannel = useMemo(() => {
        const totals = new Map<string, number>();

        filteredLeads.forEach((lead) => {
            if (lead.status !== 'won') return;
            const value = Number(lead.value) || 0;
            if (value <= 0) return;
            const canal = lead.origin?.trim() || 'Sem canal';
            totals.set(canal, (totals.get(canal) ?? 0) + value);
        });

        return Array.from(totals.entries())
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);
    }, [filteredLeads]);

    // Ranking de vendas por agente
    const salesRanking = useMemo(() => {
        // Cria mapa de usuários para acesso rápido (O(1)) e normaliza chaves
        const userMap = crmUsers.reduce((acc, user) => {
            if (user.id) {
                acc[user.id] = user.name;
                acc[user.id.toLowerCase()] = user.name;
            }
            return acc;
        }, {} as Record<string, string>);

        const salesByAgent: { [key: string]: { value: number, count: number } } = {};

        filteredLeads.forEach(lead => {
            if (lead.status === 'won' && lead.agent) {
                const agentId = lead.agent;
                if (!salesByAgent[agentId]) {
                    salesByAgent[agentId] = { value: 0, count: 0 };
                }
                salesByAgent[agentId].value += (Number(lead.value) || 0);
                salesByAgent[agentId].count += 1;
            }
        });

        return Object.entries(salesByAgent)
            .map(([id, data]) => {
                // Tenta buscar nome exato ou lowercase
                let name = userMap[id] || userMap[id.toLowerCase()];
                
                // Fallback inteligente
                if (!name) {
                    if (id === 'null' || !id) name = 'Não atribuído';
                    else name = id; // Mantém ID se realmente não encontrar
                }

                return {
                    id,
                    name,
                    value: data.value,
                    dealsCount: data.count
                };
            })
            .sort((a, b) => b.value - a.value)
            .map((agent, index) => ({ ...agent, rank: index + 1 }));
    }, [filteredLeads, crmUsers]);

    // Ranking LTV (Lifetime Value) por contato
    const ltvRanking = useMemo(() => {
        const contactLtv = contacts.map(contact => {
            const contactLeads = leads.filter(lead =>
                lead.status === 'won' &&
                lead.whatsApp === contact.phone
            );

            const totalValue = contactLeads.reduce((acc, curr) => acc + (Number(curr.value) || 0), 0);

            return {
                id: contact.id,
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                totalValue,
                dealsCount: contactLeads.length
            };
        });

        return contactLtv
            .filter(c => c.totalValue > 0)
            .sort((a, b) => b.totalValue - a.totalValue)
            .slice(0, 10);
    }, [leads, contacts]);

    return {
        salesByMonth,
        salesByChannel,
        salesRanking,
        ltvRanking
    };
};
